<template>
    <div class="dl">
        <!-- Header: project and date -->
        <header class="dl-head">
            <div class="dl-head__text">
                <h1 class="dl-head__project">{{ projectName || tr('Deliveries', 'Entregas') }}</h1>
                <div class="dl-head__sub">{{ tr('Delivery Information', 'Información de entregas') }}</div>
            </div>
            <div class="dl-date" role="group" :aria-label="tr('Date', 'Fecha')">
                <b-button variant="light" class="dl-date__step" :aria-label="tr('Previous day', 'Día anterior')" @click="shiftDate(-1)">&lsaquo;</b-button>
                <div class="dl-date__label">
                    <strong>{{ dateLabel }}</strong>
                    <button v-if="date !== today" type="button" class="dl-date__today" @click="setDate(today)">{{ tr('Today', 'Hoy') }}</button>
                </div>
                <b-button variant="light" class="dl-date__step" :aria-label="tr('Next day', 'Día siguiente')" @click="shiftDate(1)">&rsaquo;</b-button>
            </div>
        </header>

        <div v-if="state === 'loading'" class="dl-state" role="status"><b-spinner small class="mr-2" />{{ tr('Loading deliveries…', 'Cargando entregas…') }}</div>
        <div v-else-if="state === 'unavailable'" class="dl-state dl-state--box">
            <strong>{{ tr('This delivery link is not available.', 'Este enlace de entregas no está disponible.') }}</strong>
            <span>{{ tr('It may have been deactivated. Ask your contact for a new link.', 'Puede haber sido desactivado. Pide un nuevo enlace a tu contacto.') }}</span>
        </div>
        <div v-else-if="state === 'busy'" class="dl-state dl-state--box">{{ tr('Too many requests right now. Please wait a moment and try again.', 'Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.') }}</div>
        <div v-else-if="state === 'error'" class="dl-state dl-state--box">
            {{ tr("Deliveries couldn't be loaded. Check your connection.", 'No se pudieron cargar las entregas. Revisa tu conexión.') }}
            <b-button size="sm" variant="outline-primary" class="mt-2" @click="fetchDay()">{{ tr('Try again', 'Reintentar') }}</b-button>
        </div>

        <div v-else class="dl-body">
            <!-- Map: the selected delivery's floor, its pin prominent -->
            <section class="dl-map" :aria-label="tr('Delivery location', 'Ubicación de la entrega')">
                <div v-if="floorsWithDeliveries.length > 1" class="dl-floors">
                    <button v-for="f in floorsWithDeliveries" :key="f.id" type="button" class="dl-floors__btn" :class="{ 'is-active': f.id === floorId }" @click="floorId = f.id">{{ f.name }}</button>
                </div>
                <div v-if="floor && floor.imageUrl" ref="frame" class="dl-map__frame">
                    <div class="dl-map__drawing" :style="drawingStyle">
                        <img :src="floor.imageUrl" :alt="tr('Floor plan: ', 'Plano: ') + floor.name" class="dl-map__img" @load="onImage" />
                        <button
                            v-for="p in pinsOnFloor"
                            :key="p.key"
                            type="button"
                            class="dl-pin"
                            :class="{ 'is-selected': p.id === selectedId }"
                            :style="{ left: (p.x * 100) + '%', top: (p.y * 100) + '%' }"
                            :aria-label="tr('Delivery: ', 'Entrega: ') + p.what"
                            @click="select(p.id)"
                        ><font-awesome-icon :icon="['fas', 'truck']" /></button>
                    </div>
                </div>
                <p v-else-if="deliveries.length" class="dl-note">{{ tr('No floor plan is available for this delivery.', 'No hay un plano disponible para esta entrega.') }}</p>
                <p v-if="selected && !selected.pins.length" class="dl-note">{{ tr('No location pin is set for this delivery - see the location below.', 'Esta entrega no tiene un punto en el plano; mira la ubicación abajo.') }}</p>
            </section>

            <aside class="dl-side">
                <!-- Jobsite: only what admins entered in Settings → Jobsite -->
                <section v-if="site" class="dl-card dl-site" :aria-label="tr('Jobsite', 'Obra')">
                    <h2 class="dl-card__title"><font-awesome-icon :icon="['fas', 'map-marker-alt']" aria-hidden="true" />{{ tr('Getting There', 'Cómo llegar') }}</h2>
                    <template v-if="site.address">
                        <div class="dl-site__label">{{ tr('Jobsite address', 'Dirección de la obra') }}</div>
                        <p class="dl-site__text">{{ site.address }}</p>
                        <a :href="mapsUrl" target="_blank" rel="noopener noreferrer" class="btn btn-outline-primary btn-block dl-site__maps">{{ tr('Open in Maps', 'Abrir en Mapas') }}</a>
                    </template>
                    <template v-if="site.instructions">
                        <div class="dl-site__label" :class="{ 'mt-3': site.address }">{{ tr('Gate and unloading instructions', 'Instrucciones de entrada y descarga') }}</div>
                        <p class="dl-site__text">{{ site.instructions }}</p>
                    </template>
                </section>

                <!-- Details of the selected delivery -->
                <section v-if="selected" class="dl-card" :aria-label="tr('Delivery details', 'Detalles de la entrega')">
                    <h2 class="dl-card__title">{{ tr('Delivery Details', 'Detalles de la entrega') }}</h2>
                    <dl class="dl-fields">
                        <dt>{{ tr('Delivery', 'Entrega') }}</dt><dd class="dl-strong">{{ selected.what || '—' }}</dd>
                        <dt>{{ tr('Scheduled', 'Programada') }}</dt><dd>{{ dateLabel }}<template v-if="timeText(selected)"> · {{ timeText(selected) }}</template></dd>
                        <dt>{{ tr('Location', 'Ubicación') }}</dt><dd>{{ whereText(selected) || '—' }}</dd>
                        <template v-if="selected.resources.length">
                            <dt>{{ tr('Reserved', 'Reservado') }}</dt>
                            <dd><span v-for="r in selected.resources" :key="r.name" class="dl-resource">{{ r.name }}<template v-if="timeText(r)"> · {{ timeText(r) }}</template></span></dd>
                        </template>
                        <dt>{{ tr('Contact', 'Contacto') }}</dt>
                        <dd>
                            <span class="dl-strong">{{ selected.contact.name || '—' }}</span><span v-if="selected.contact.company"> · {{ selected.contact.company }}</span>
                            <span class="dl-muted d-block">{{ tr('Foreman for this delivery', 'Capataz de esta entrega') }}</span>
                        </dd>
                    </dl>
                    <a v-if="selected.contact.phone" :href="'tel:' + selected.contact.phone" class="btn btn-primary btn-block dl-call">{{ tr('Call Foreman', 'Llamar al capataz') }}</a>
                </section>

                <!-- The day's deliveries -->
                <section class="dl-card" :aria-label="tr('Deliveries', 'Entregas')">
                    <h2 class="dl-card__title">{{ date === today ? tr("Today's Deliveries", 'Entregas de hoy') : tr('Deliveries', 'Entregas') }} <span v-if="deliveries.length" class="dl-count">{{ deliveries.length }}</span></h2>
                    <p v-if="!deliveries.length" class="dl-muted mb-0">{{ tr('No deliveries scheduled for this day.', 'No hay entregas programadas para este día.') }}</p>
                    <ul v-else class="dl-list">
                        <li v-for="d in deliveries" :key="d.id">
                            <button type="button" class="dl-list__row" :class="{ 'is-selected': d.id === selectedId }" :aria-pressed="d.id === selectedId ? 'true' : 'false'" @click="select(d.id)">
                                <span class="dl-list__time">{{ timeText(d) || '—' }}</span>
                                <span class="dl-list__main">
                                    <span class="dl-strong">{{ d.what || tr('Delivery', 'Entrega') }}</span>
                                    <span class="dl-muted">{{ whereText(d) }}</span>
                                </span>
                            </button>
                        </li>
                    </ul>
                </section>
                <p class="dl-foot">{{ tr('View only · updates automatically', 'Solo lectura · se actualiza automáticamente') }}</p>
            </aside>
        </div>
    </div>
</template>

<script>
// Delivery Link: a no-login, read-only page for drivers and suppliers. All
// data comes from the deliveryLinkDay function, which checks the link and
// returns only the approved delivery fields for one date (never Firestore
// documents). Refreshes every 45s while the page is visible and right away
// when the date changes.
import firebase from 'firebase/app'
import 'firebase/functions'
import moment from 'moment'

const REFRESH_MS = 45000

export default {
    name: 'DeliveryLinkView',
    props: { token: { type: String, default: '' } },

    data() {
        const today = moment().format('YYYY-MM-DD')
        return { today, date: today, state: 'loading', projectName: '', site: null, floors: [], deliveries: [], selectedId: null, floorId: null, aspect: null, frameWidth: 0, requestId: 0 }
    },

    computed: {
        lang() { const l = (navigator.language || 'en').toLowerCase(); return l.startsWith('es') ? 'es' : 'en' },
        dateLabel() { return moment(this.date).locale(this.lang).format('ddd, MMM D') },
        // A plain maps search for the address the admin typed - nothing is
        // geocoded or sent from our side.
        mapsUrl() { return this.site && this.site.address ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(this.site.address.replace(/\n/g, ', ')) : '' },
        selected() { return this.deliveries.find(d => d.id === this.selectedId) || null },
        floorsWithDeliveries() {
            const ids = new Set(this.deliveries.map(d => d.floorId).filter(Boolean))
            return this.floors.filter(f => ids.has(f.id))
        },
        floor() { return this.floors.find(f => f.id === this.floorId) || null },
        pinsOnFloor() {
            const out = []
            this.deliveries.filter(d => d.floorId === this.floorId).forEach(d => d.pins.forEach((p, i) => out.push({ key: `${d.id}-${i}`, id: d.id, what: d.what, x: p.x, y: p.y })))
            // The selected delivery's pins on top.
            return out.sort((a, b) => (a.id === this.selectedId) - (b.id === this.selectedId))
        },
        // Phones: a wide drawing gets a useful height and pans sideways
        // (proportions kept; pins are positioned in percent).
        drawingStyle() {
            if (!this.aspect || !this.frameWidth || this.frameWidth >= 700) return null
            const minHeight = 300
            const width = Math.min(Math.round(minHeight * this.aspect), this.frameWidth * 3)
            return width > this.frameWidth * 1.05 ? { width: width + 'px' } : null
        },
    },

    watch: {
        // Another link opened in the same tab: start over with it.
        token() { this.selectedId = null; this.floorId = null; this.fetchDay() },
    },

    created() {
        this.fetchDay()
        this.timer = setInterval(() => { if (document.visibilityState === 'visible') this.fetchDay({ quiet: true }) }, REFRESH_MS)
        this.onVisible = () => { if (document.visibilityState === 'visible') this.fetchDay({ quiet: true }) }
        document.addEventListener('visibilitychange', this.onVisible)
        this.onResize = () => { this.measure() }
        window.addEventListener('resize', this.onResize)
    },

    beforeDestroy() {
        clearInterval(this.timer)
        document.removeEventListener('visibilitychange', this.onVisible)
        window.removeEventListener('resize', this.onResize)
    },

    methods: {
        tr(en, es) { return this.lang === 'es' ? es : en },
        timeText(item) {
            const f = t => (t ? moment(t, 'HH:mm:ss').format(moment(t, 'HH:mm:ss').minutes() ? 'h:mm A' : 'h A') : '')
            const s = f(item.startTime), e = f(item.endTime)
            return s && e ? `${s}–${e}` : s || e
        },
        whereText(d) { return [d.floorName, d.location].filter(Boolean).join(' · ') },

        setDate(date) { this.date = date; this.selectedId = null; this.fetchDay() },
        shiftDate(days) { this.setDate(moment(this.date).add(days, 'days').format('YYYY-MM-DD')) },

        async fetchDay({ quiet = false } = {}) {
            const id = ++this.requestId
            if (!quiet) this.state = 'loading'
            try {
                const { data } = await firebase.functions().httpsCallable('deliveryLinkDay')({ token: this.token, date: this.date })
                if (id !== this.requestId) return
                this.projectName = (data.project && data.project.name) || ''
                this.site = data.site || null
                this.floors = data.floors || []
                this.deliveries = data.deliveries || []
                if (!this.deliveries.some(d => d.id === this.selectedId)) {
                    // Default: the next delivery still to come today, else the first.
                    const now = moment().format('HH:mm:ss')
                    const next = this.date === this.today ? this.deliveries.find(d => !d.endTime || d.endTime >= now) : null
                    this.selectedId = (next || this.deliveries[0] || {}).id || null
                }
                this.pickFloor()
                this.state = 'ready'
            } catch (error) {
                if (id !== this.requestId) return
                const code = error && error.code
                if (quiet && this.state === 'ready' && code !== 'functions/not-found') return // keep showing the last data
                this.state = code === 'functions/not-found' ? 'unavailable' : code === 'functions/resource-exhausted' ? 'busy' : 'error'
            }
        },

        pickFloor() {
            const d = this.selected
            if (d && d.floorId && this.floors.some(f => f.id === d.floorId)) { this.floorId = d.floorId; return }
            if (this.floorId && this.floorsWithDeliveries.some(f => f.id === this.floorId)) return
            this.floorId = (this.floorsWithDeliveries[0] || this.floors[0] || {}).id || null
        },

        select(id) {
            this.selectedId = id
            this.pickFloor()
            // Bring the selected pin into view (drawing frame and page).
            this.$nextTick(() => {
                const pin = this.$el.querySelector('.dl-pin.is-selected')
                if (pin) pin.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            })
        },

        onImage(event) {
            const img = event.target
            this.aspect = img.naturalHeight ? img.naturalWidth / img.naturalHeight : null
            this.measure()
        },
        measure() { this.frameWidth = this.$refs.frame ? this.$refs.frame.clientWidth : 0 },
    },
}
</script>

<style scoped>
.dl { max-width: 1200px; margin: 0 auto; padding: 0 16px 24px; color: #1f2933; }
.dl-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px 16px; padding: 16px 0 12px; border-bottom: 1px solid #e4e7eb; }
.dl-head__project { margin: 0; font-size: 1.5rem; font-weight: 700; line-height: 1.2; }
.dl-head__sub { color: #52606d; font-size: 1rem; }
.dl-date { display: flex; align-items: center; gap: 6px; }
.dl-date__step { min-width: 44px; min-height: 44px; font-size: 1.4rem; line-height: 1; }
.dl-date__label { display: flex; flex-direction: column; align-items: center; min-width: 120px; text-align: center; }
.dl-date__today { padding: 0; border: 0; background: none; color: #007bff; font-size: max(14px, 0.85rem); }

.dl-state { padding: 24px 0; color: #52606d; }
.dl-state--box { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; margin-top: 16px; padding: 16px; border-radius: 12px; background: #f3f5f8; color: #1f2933; }

.dl-body { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; margin-top: 14px; }
.dl-map, .dl-side { min-width: 0; }
@media (min-width: 992px) { .dl-body { grid-template-columns: minmax(0, 1.6fr) minmax(320px, 1fr); align-items: start; } }

.dl-floors { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.dl-floors__btn { min-height: 44px; padding: 4px 12px; border: 1px solid #d5dbe3; border-radius: 18px; background: #fff; color: #1f2933; font-size: max(14px, 0.9rem); }
.dl-floors__btn.is-active { border-color: #2563eb; background: #e8f0fe; color: #1d4ed8; font-weight: 600; }

.dl-map__frame { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; overscroll-behavior-x: contain; border: 1px solid #dee2e6; border-radius: 10px; background: #fff; }
.dl-map__drawing { position: relative; }
.dl-map__img { display: block; width: 100%; height: auto; max-width: none; }
.dl-pin {
    position: absolute; transform: translate(-50%, -100%);
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; padding: 0;
    border: 2px solid #fff; border-radius: 50% 50% 50% 4px;
    background: #64748b; color: #fff; font-size: max(14px, 0.9rem);
    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.3);
    rotate: -45deg;
}
.dl-pin > * { rotate: 45deg; }
.dl-pin.is-selected { width: 46px; height: 46px; background: #2563eb; font-size: 1.15rem; z-index: 2; box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.25), 0 3px 10px rgba(16, 24, 40, 0.35); }
.dl-pin:focus-visible { outline: 3px solid rgba(0, 123, 255, 0.6); outline-offset: 2px; }
.dl-note { margin: 8px 0 0; padding: 10px 12px; border-radius: 8px; background: #fff8e6; color: #7a4b00; font-size: max(14px, 0.95rem); }

.dl-site__label { font-size: max(15px, 0.95rem); font-weight: 700; color: #4b5563; }
.dl-site__text { margin: 2px 0 0; font-size: 1rem; white-space: pre-line; overflow-wrap: anywhere; }
.dl-site__maps { margin-top: 10px; min-height: 44px; display: flex; align-items: center; justify-content: center; font-weight: 600; }
.dl-side { display: flex; flex-direction: column; gap: 12px; }
.dl-card { padding: 14px 16px; border: 1px solid #e4e7eb; border-radius: 14px; background: #fff; }
.dl-card__title { display: flex; align-items: center; gap: 8px; margin: 0 0 10px; font-size: 1.1rem; font-weight: 700; }
.dl-count { min-width: 22px; padding: 0 7px; border-radius: 10px; background: #e8f0fe; color: #2563eb; font-size: max(14px, 0.8rem); text-align: center; }
.dl-fields { display: grid; grid-template-columns: auto 1fr; gap: 8px 14px; margin: 0; font-size: 1rem; }
.dl-fields dt { color: #52606d; font-weight: 600; font-size: max(14px, 0.9rem); padding-top: 1px; }
.dl-fields dd { margin: 0; min-width: 0; overflow-wrap: anywhere; }
.dl-strong { font-weight: 700; }
.dl-muted { color: #52606d; font-size: max(14px, 0.9rem); }
.dl-resource { display: inline-block; margin: 0 6px 4px 0; padding: 2px 8px; border-radius: 10px; background: #f1ebfe; color: #6d28d9; font-size: max(14px, 0.88rem); font-weight: 600; }
.dl-call { margin-top: 12px; min-height: 48px; font-weight: 700; font-size: 1.05rem; display: flex; align-items: center; justify-content: center; }

.dl-list { margin: 0; padding: 0; list-style: none; }
.dl-list li + li { border-top: 1px solid #eef0f3; }
.dl-list__row { display: flex; align-items: center; gap: 12px; width: 100%; min-height: 56px; padding: 8px 6px; border: 0; border-radius: 8px; background: none; text-align: left; color: inherit; font-size: 1rem; line-height: 1.3; font-family: inherit; }
.dl-list__row:hover { background: #f5f8fc; }
.dl-list__row.is-selected { background: #e8f0fe; }
.dl-list__row:focus-visible { outline: 3px solid rgba(0, 123, 255, 0.45); outline-offset: -3px; }
.dl-list__time { flex: none; min-width: 96px; font-size: max(14px, 0.92rem); font-weight: 600; font-variant-numeric: tabular-nums; color: #1d4ed8; }
.dl-list__main .dl-strong { font-size: 1rem; }
.dl-list__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.dl-foot { margin: 0; color: #6c757d; font-size: max(14px, 0.85rem); text-align: center; }

@media (max-width: 575px) {
    .dl { padding: 0 12px 20px; }
    .dl-head__project { font-size: 1.3rem; }
    .dl-date { width: 100%; justify-content: space-between; }
}
</style>
